import { create } from '@bufbuild/protobuf'
import { type ConnectError } from '@connectrpc/connect'
import { useQuery } from '@connectrpc/connect-query'
import { getSectionRelease } from '@ebalance/proto/bffuser/v1/section_release_service-SectionReleaseService_connectquery'
import {
  GetSectionReleaseRequestSchema,
  type GetSectionReleaseResponse,
} from '@ebalance/proto/bffuser/v1/section_release_service_pb'
import { WorkflowSection } from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'
import type { UseQueryResult } from '@tanstack/react-query'

const REQUEST = create(GetSectionReleaseRequestSchema, {})

/**
 * 新しい雛形・業務ワークフローが今取れる計画断面 (公開済みの集合) を取得する。
 * flag は frontend で評価せず、 この集合だけを見る。 取得のたびに最新を見る。
 */
export function useSectionRelease(): UseQueryResult<
  GetSectionReleaseResponse,
  ConnectError
> {
  return useQuery(getSectionRelease, REQUEST, {
    throwOnError: false,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  })
}

export type SectionReleaseGate =
  | { state: 'pending' }
  | { state: 'ready'; released: ReadonlySet<WorkflowSection> }

/** 単日は公開状態に依らず常に選べる (spec AC-WFT-006)。 取得中・取得失敗の選択肢の基準。 */
export const SINGLE_DAY_ONLY: ReadonlySet<WorkflowSection> = new Set([
  WorkflowSection.SINGLE_DAY,
])

/**
 * 公開済みの断面の gate。
 * - 取得失敗は単日だけ公開と同じ (fail-closed)
 * - この mount で取得し終えるまでは `pending` (呼び出し側は待つ)
 * - 再取得中は直前の値を保つ
 */
export function sectionReleaseGate(
  result: UseQueryResult<GetSectionReleaseResponse, ConnectError>,
): SectionReleaseGate {
  if (result.isError) return { state: 'ready', released: SINGLE_DAY_ONLY }
  if (!result.isFetchedAfterMount || result.data === undefined) {
    return { state: 'pending' }
  }
  return {
    state: 'ready',
    released: new Set(result.data.releasedSections),
  }
}
